import { useState, useEffect } from 'react';
import { X, ExternalLink, Building2, Film, FileCode, File, ListChecks, Shield, User, ChevronRight, ArrowRightLeft, Link2 } from 'lucide-react';
import apiClient from '@/lib/api';
import { toast } from 'sonner';

const TYPE_CONFIG = {
  PPL: { icon: User, label: 'Member', color: '#c026d3', bg: 'bg-fuchsia-50', border: 'border-fuchsia-200' },
  ORG: { icon: Building2, label: 'Organization', color: '#0891b2', bg: 'bg-cyan-50', border: 'border-cyan-200' },
  PRJ: { icon: Film, label: 'Project', color: '#2563eb', bg: 'bg-blue-50', border: 'border-blue-200' },
  SCR: { icon: FileCode, label: 'Script', color: '#16a34a', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  FIL: { icon: File, label: 'File', color: '#d97706', bg: 'bg-amber-50', border: 'border-amber-200' },
  LST: { icon: ListChecks, label: 'List', color: '#9333ea', bg: 'bg-purple-50', border: 'border-purple-200' },
  REG: { icon: Shield, label: 'Registry', color: '#C4A35A', bg: 'bg-yellow-50', border: 'border-yellow-200' },
  LNK: { icon: Link2, label: 'Link', color: '#6366f1', bg: 'bg-indigo-50', border: 'border-indigo-200' },
};

const COLL_TO_TYPE = {
  members: 'PPL', organizations: 'ORG', projects: 'PRJ',
  scripts: 'SCR', files: 'FIL', lists: 'LST', registry: 'REG', links: 'LNK',
  sibling_scripts: 'SCR', sibling_files: 'FIL', sibling_lists: 'LST',
  parent_entity: 'PRJ',
};

const COLL_LABELS = {
  members: 'People', organizations: 'Organizations', projects: 'Projects',
  scripts: 'Scripts', files: 'Files', lists: 'Lists', registry: 'Registry', links: 'Links',
  sibling_scripts: 'Other Scripts', sibling_files: 'Other Files', sibling_lists: 'Other Lists',
  parent_entity: 'Belongs To', trust_score: 'Trust Score',
};

const IdLink = ({ id, onClick }) => {
  if (!id) return <span className="text-[#D8D3CB]">—</span>;
  const prefix = id.split('-')[1];
  const config = TYPE_CONFIG[prefix] || {};
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(id); }}
      className="inline-flex items-center gap-1 text-[11px] font-mono font-bold hover:underline transition-colors"
      style={{ color: config.color || '#C4A35A' }}
      data-testid={`id-link-${id}`}
    >
      {id}<ExternalLink size={10} />
    </button>
  );
};

/* ═══════════════════════════════
   CONNECTED ENTITIES SECTION
   ═══════════════════════════════ */
const ConnectedSection = ({ collKey, items, onNavigate }) => {
  const [expanded, setExpanded] = useState(false);
  if (!items || items.length === 0) return null;
  const typeKey = COLL_TO_TYPE[collKey] || 'PRJ';
  const config = TYPE_CONFIG[typeKey] || {};
  const Icon = config.icon || Film;
  const label = COLL_LABELS[collKey] || collKey;
  const shown = expanded ? items : items.slice(0, 5);

  return (
    <div className="mb-3" data-testid={`connected-${collKey}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={12} style={{ color: config.color }} />
        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: config.color }}>{label}</span>
        <span className="text-[9px] text-[#9B9589] bg-[#F8F7F5] px-1.5 py-0.5 border border-[#D8D3CB] ainaa-num">{items.length}</span>
      </div>
      <div className="space-y-0.5">
        {shown.map((item, i) => {
          const itemId = item.ainaa_id || item.registry_id;
          const itemLabel = item.name || item.title || '';
          const sub = item.department || item.profession || item.type || item.version || item.work_type || item.link_type || '';
          const isExtLink = collKey === 'links' && item.url;
          if (isExtLink) {
            return (
              <a
                key={itemId || i}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-all group border ${config.border} ${config.bg} hover:opacity-80`}
                data-testid={`connected-item-${itemId}`}
              >
                <span className="text-[10px] font-mono font-bold min-w-0 truncate" style={{ color: config.color }}>{itemId}</span>
                <span className="text-xs text-[#0F1B2D] truncate flex-1">{itemLabel}</span>
                <span className="text-[9px] text-[#9B9589] flex-shrink-0">{sub}</span>
                <ExternalLink size={10} className="text-blue-500 flex-shrink-0" />
              </a>
            );
          }
          return (
            <button
              key={itemId || i}
              onClick={() => onNavigate(itemId)}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-all group border ${config.border} ${config.bg} hover:opacity-80`}
              data-testid={`connected-item-${itemId}`}
            >
              <span className="text-[10px] font-mono font-bold min-w-0 truncate" style={{ color: config.color }}>{itemId}</span>
              <span className="text-xs text-[#0F1B2D] truncate flex-1">{itemLabel}</span>
              {sub && <span className="text-[9px] text-[#9B9589] flex-shrink-0">{sub}</span>}
              {item.ip_transfer && <ArrowRightLeft size={10} className="text-amber-600 flex-shrink-0" />}
              <ChevronRight size={10} className="text-[#D8D3CB] group-hover:text-[#0F1B2D] flex-shrink-0" />
            </button>
          );
        })}
      </div>
      {items.length > 5 && (
        <button onClick={() => setExpanded(!expanded)} className="text-[10px] font-bold text-[#C4A35A] mt-1 hover:underline" data-testid={`expand-${collKey}`}>
          {expanded ? 'Show less' : `Show all ${items.length}`}
        </button>
      )}
    </div>
  );
};

/* ═══════════════════════════════
   FIELD DISPLAY
   ═══════════════════════════════ */
const Field = ({ label, children, mono }) => (
  <div className="flex gap-3 py-1.5 border-b border-[#F0EDE8] last:border-0">
    <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#9B9589] w-[120px] flex-shrink-0 pt-0.5">{label}</span>
    <span className={`text-sm text-[#0F1B2D] min-w-0 ${mono ? 'font-mono text-[11px]' : ''}`}>{children || '—'}</span>
  </div>
);

/* ═══════════════════════════════
   MAIN MODAL
   ═══════════════════════════════ */
const EntityDetailModal = ({ ainaaId, onClose, onNavigate }) => {
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!ainaaId) return;
    setLoading(true);
    apiClient.get(`/admin/entity-detail/${ainaaId}`)
      .then(({ data }) => setEntity(data))
      .catch(() => toast.error(`Failed to load ${ainaaId}`))
      .finally(() => setLoading(false));
  }, [ainaaId]);

  if (!ainaaId) return null;

  const etype = entity?._entity_type;
  const config = TYPE_CONFIG[etype] || TYPE_CONFIG.PRJ;
  const Icon = config.icon || Film;
  const connected = entity?._connected || {};

  const nav = (id) => {
    setHistory(h => [...h, ainaaId]);
    onNavigate(id);
  };
  const goBack = () => {
    if (history.length > 0) {
      const prev = history[history.length - 1];
      setHistory(h => h.slice(0, -1));
      onNavigate(prev);
    }
  };

  // Order connected sections for display
  const connOrder = ['organizations', 'projects', 'members', 'scripts', 'files', 'lists', 'registry', 'links', 'source_entity', 'target_entity', 'sibling_scripts', 'sibling_files', 'sibling_lists'];

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="entity-detail-modal">
      <div className="bg-white border-2 border-[#0F1B2D] w-full max-w-5xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex-shrink-0 border-b-2 border-[#0F1B2D] px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {history.length > 0 && (
              <button onClick={goBack} className="text-[10px] font-bold text-[#9B9589] hover:text-[#0F1B2D] mr-1" data-testid="entity-detail-back">BACK</button>
            )}
            <div className="w-7 h-7 flex items-center justify-center border" style={{ borderColor: config.color, color: config.color }}>
              <Icon size={14} />
            </div>
            <div>
              <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: config.color }}>{config.label}</span>
              <span className="block text-[11px] font-mono font-bold text-[#0F1B2D]">{ainaaId}</span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-[#9B9589] hover:text-[#0F1B2D]" data-testid="entity-detail-close">
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-16 text-[#9B9589]">Loading...</div>
          ) : !entity ? (
            <div className="text-center py-16 text-[#9B9589]">Entity not found</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* LEFT: Entity details */}
              <div className="p-5 border-b lg:border-b-0 lg:border-r border-[#D8D3CB]">
                {(entity.name || entity.title) && (
                  <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#0F1B2D] mb-3">{entity.name || entity.title}</h3>
                )}
                <EntityFields entity={entity} etype={etype} onNav={nav} />
              </div>

              {/* RIGHT: Connected entities graph */}
              <div className="p-5">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#9B9589] mb-3">Connected Entities</h4>
                {connOrder.map(key => (
                  <ConnectedSection key={key} collKey={key} items={connected[key]} onNavigate={nav} />
                ))}
                {connected.trust_score && (
                  <div className="mb-3 p-3 border border-[#C4A35A] bg-[#FEFCF3]">
                    <span className="text-[9px] font-bold uppercase tracking-wider text-[#C4A35A]">Trust Score</span>
                    <span className="block text-2xl font-bold text-[#0F1B2D] mt-1 ainaa-num">{connected.trust_score.overall_score}</span>
                    <div className="flex gap-4 mt-1 text-[10px] text-[#9B9589] ainaa-num">
                      <span>Work: {connected.trust_score.work_history}</span>
                      <span>Finance: {connected.trust_score.financial_standing}</span>
                      <span>Peer: {connected.trust_score.peer_reviews}</span>
                    </div>
                  </div>
                )}
                {Object.values(connected).every(v => !v || (Array.isArray(v) && v.length === 0)) && !connected.trust_score && (
                  <p className="text-sm text-[#9B9589]">No connected entities found.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════
   PER-TYPE FIELD RENDERERS
   ═══════════════════════════════ */
const EntityFields = ({ entity, etype, onNav }) => {
  switch (etype) {
    case 'PRJ': return <ProjectFields e={entity} onNav={onNav} />;
    case 'SCR': return <ScriptFields e={entity} onNav={onNav} />;
    case 'FIL': return <FileFields e={entity} onNav={onNav} />;
    case 'LST': return <ListFields e={entity} onNav={onNav} />;
    case 'ORG': return <OrgFields e={entity} onNav={onNav} />;
    case 'PPL': return <MemberFields e={entity} onNav={onNav} />;
    case 'REG': return <RegistryFields e={entity} onNav={onNav} />;
    case 'LNK': return <LinkFields e={entity} onNav={onNav} />;
    default: return <Field label="ID" mono>{entity.ainaa_id || entity.registry_id}</Field>;
  }
};

const ProjectFields = ({ e, onNav }) => (
  <>
    <Field label="AINAA ID" mono>{e.ainaa_id}</Field>
    <Field label="Type">{e.type}</Field>
    <Field label="Status">{e.status}</Field>
    <Field label="Budget">{e.budget ? `INR ${Number(e.budget).toLocaleString()}` : null}</Field>
    <Field label="Owner"><IdLink id={e.owner_id} onClick={onNav} />{e._owner_name && <span className="text-xs text-[#9B9589] ml-2">{e._owner_name}</span>}</Field>
    <Field label="Start">{e.start_date}</Field>
    <Field label="End">{e.end_date}</Field>
    <Field label="Description">{e.description}</Field>
    <Field label="Tags">{e.tags?.join(', ')}</Field>
    <Field label="Team">{e.team_members?.length || 0} members</Field>
  </>
);

const ScriptFields = ({ e, onNav }) => (
  <>
    <Field label="AINAA ID" mono>{e.ainaa_id}</Field>
    <Field label="Version">{e.version}</Field>
    <Field label="Status">{e.status}</Field>
    <Field label="Project"><IdLink id={e.project_id} onClick={onNav} />{e._project_name && <span className="text-xs text-[#9B9589] ml-2">{e._project_name}</span>}</Field>
    <Field label="Author">
      {e.author_id?.startsWith('AINAA-') ? (
        <span><IdLink id={e.author_id} onClick={onNav} />{(e.author_name || e._author_name) && <span className="text-xs text-[#9B9589] ml-2">{e.author_name || e._author_name}</span>}</span>
      ) : <span>{e.author_name || e._author_name || e.author_id || '—'}</span>}
    </Field>
    <Field label="Description">{e.description}</Field>
    <Field label="Created">{e.created_at ? new Date(e.created_at).toLocaleString() : null}</Field>
  </>
);

const FileFields = ({ e, onNav }) => (
  <>
    <Field label="AINAA ID" mono>{e.ainaa_id}</Field>
    <Field label="Type">{e.type}</Field>
    <Field label="Size">{e.size_bytes ? `${(e.size_bytes / 1024).toFixed(1)} KB` : null}</Field>
    <Field label="MIME">{e.mime_type}</Field>
    <Field label="Project"><IdLink id={e.project_id} onClick={onNav} />{e._project_name && <span className="text-xs text-[#9B9589] ml-2">{e._project_name}</span>}</Field>
    <Field label="Tags">{e.tags?.join(', ')}</Field>
    <Field label="Uploaded By">{e.uploaded_by}</Field>
    <Field label="Created">{e.created_at ? new Date(e.created_at).toLocaleString() : null}</Field>
  </>
);

const ListFields = ({ e, onNav }) => (
  <>
    <Field label="AINAA ID" mono>{e.ainaa_id}</Field>
    <Field label="Type">{e.type}</Field>
    <Field label="Department">{e.department}</Field>
    <Field label="Visibility">{e.visibility}</Field>
    <Field label="Project"><IdLink id={e.project_id} onClick={onNav} />{e._project_name && <span className="text-xs text-[#9B9589] ml-2">{e._project_name}</span>}</Field>
    <Field label="Organization"><IdLink id={e.org_id} onClick={onNav} />{e._org_name && <span className="text-xs text-[#9B9589] ml-2">{e._org_name}</span>}</Field>
    <Field label="Members">{e.items?.length || 0}</Field>
    <Field label="Description">{e.description}</Field>
  </>
);

const OrgFields = ({ e }) => (
  <>
    <Field label="AINAA ID" mono>{e.ainaa_id}</Field>
    <Field label="Type">{e.type}</Field>
    <Field label="Industry">{e.industry}</Field>
    <Field label="Status">{e.status}</Field>
    <Field label="Email">{e.email}</Field>
    <Field label="Website">{e.website}</Field>
    <Field label="Description">{e.description}</Field>
    <Field label="Members">{e.members?.length || 0}</Field>
  </>
);

const MemberFields = ({ e }) => (
  <>
    <Field label="AINAA ID" mono>{e.ainaa_id}</Field>
    <Field label="Email">{e.email}</Field>
    <Field label="Profession">{e.profession}</Field>
    <Field label="Field">{e.field}</Field>
    <Field label="Department">{e.department}</Field>
    <Field label="Experience">{e.experience_years ? `${e.experience_years} years` : null}</Field>
    <Field label="Status">{e.status}</Field>
    <Field label="Phone">{e.phone}</Field>
    <Field label="Country">{e.country}</Field>
  </>
);

const RegistryFields = ({ e, onNav }) => (
  <>
    <Field label="Registry ID" mono>{e.registry_id}</Field>
    <Field label="Hash" mono>{e.verification_hash}</Field>
    <Field label="Work Type">{e.work_type}</Field>
    <Field label="Status">{e.status}</Field>
    <Field label="Owner">
      {e.owner_ainaa_id ? (
        <span>{e.owner_name || e._owner_name || ''} <IdLink id={e.owner_ainaa_id} onClick={onNav} /></span>
      ) : '—'}
    </Field>
    <Field label="Entity"><IdLink id={e.entity_ainaa_id} onClick={onNav} /></Field>
    <Field label="Project"><IdLink id={e.project_ainaa_id} onClick={onNav} /></Field>
    <Field label="Description">{e.description}</Field>
    <Field label="Registered">{e.registered_at ? new Date(e.registered_at).toLocaleString() : null}</Field>
    {e.ip_transfer ? (
      <div className="mt-3 p-3 border border-[#C4A35A] bg-[#FEFCF3]">
        <span className="text-[9px] font-bold uppercase tracking-wider text-[#C4A35A] block mb-2">IP Rights Transfer</span>
        <Field label="Type">{e.ip_transfer.transfer_type?.replace(/_/g, ' ')}</Field>
        <Field label="To">
          {e.ip_transfer.transferred_to_name} <IdLink id={e.ip_transfer.transferred_to} onClick={onNav} />
        </Field>
        <Field label="Date">{e.ip_transfer.transfer_date ? new Date(e.ip_transfer.transfer_date).toLocaleDateString() : null}</Field>
        <Field label="Consideration">{e.ip_transfer.consideration}</Field>
      </div>
    ) : (
      <Field label="IP Rights"><span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 bg-gray-50 text-gray-500 border border-gray-200">Original Owner</span></Field>
    )}
  </>
);

const LinkFields = ({ e, onNav }) => (
  <>
    <Field label="AINAA ID" mono>{e.ainaa_id}</Field>
    <Field label="Type"><span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200">{e.link_type?.replace(/_/g, ' ')}</span></Field>
    <Field label="URL">
      {e.url ? <a href={e.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline break-all">{e.url}</a> : '—'}
    </Field>
    <Field label="Entity"><IdLink id={e.entity_id} onClick={onNav} /></Field>
    <Field label="Status">{e.status}</Field>
    <Field label="Description">{e.description}</Field>
    <Field label="Created">{e.created_at ? new Date(e.created_at).toLocaleString() : null}</Field>
  </>
);

export { IdLink };
export default EntityDetailModal;
